import { explorerAddressUrl, explorerTxUrl, SWEEP_RULE_TEXT } from '@afterhours/core/treasury';
import { DataAge } from '../components/DataAge';
import { SiteFooter, SiteHeader } from '../components/SiteChrome';
import { useSnapshot } from '../data/useSnapshot';
import { BRAND } from '../lib/brand';
import { count, DASH, isoUtc, usd } from '../lib/format';

/**
 * Honest numbers, ported from `apps/web/app/honest-numbers/page.tsx`.
 *
 * The contract is unchanged and is the whole point of the page: every figure
 * was counted from a stored row, carries the table it came from and the
 * timestamp of the newest row behind it, and renders as an em dash when it
 * cannot be sourced.
 *
 * One honest difference belongs on this page more than anywhere else. The
 * numbers here were counted when the agent built the snapshot, not when you
 * loaded the page, so `<DataAge/>` is rendered directly under the lede and the
 * "generated" line names the snapshot's own timestamp rather than the clock in
 * your browser. A page about not overstating what we know should not overstate
 * how fresh it is.
 */

/** A hash rendered in full is unreadable; the link carries the full value. */
function shortHash(hash: string): string {
  return hash.length > 18 ? `${hash.slice(0, 10)}…${hash.slice(-8)}` : hash;
}

export function HonestNumbers() {
  const { snapshot, status } = useSnapshot();

  const rows = snapshot?.honestNumbers ?? [];
  const sweeps = snapshot?.sweeps ?? [];
  const swept = sweeps.reduce((s, r) => s + (r.usd ?? 0), 0);

  return (
    <div className="page">
      <div className="grid" />
      <SiteHeader active="honest" />

      <div className="hn">
        <h1>Honest numbers</h1>
        <p className="lede">
          What we can actually count, counted from our own database. Each row names the table it
          came from and the timestamp of the newest row behind it. Figures we cannot source are left
          blank rather than estimated.{' '}
          {snapshot ? `Counted ${snapshot.generatedAtIso}.` : 'No snapshot loaded.'}
        </p>

        <DataAge />

        {/*
          The table arrives with the first fetch and used to push everything
          below it down (CLS 0.44 on mobile, QA 29 Sep). The wrapper holds the
          table's height while the snapshot is loading.
        */}
        <div className={rows.length === 0 && status === 'loading' ? 'hn-numbers reserve' : 'hn-numbers'}>
        {rows.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th className="num">Value</th>
                <th>Source</th>
                <th>As of</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label}>
                  <td className="label">{r.label}</td>
                  <td className={r.value === null ? 'num none' : 'num'}>{count(r.value)}</td>
                  <td className="src">
                    {r.source}
                    {r.note ? <span className="note">{r.note}</span> : null}
                  </td>
                  <td className="asof">{r.asOf === null ? DASH : isoUtc(r.asOf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : status === 'loading' ? (
          // The empty-state copy used to flash for a few seconds on every
          // direct visit, before the first fetch had even returned.
          <p className="lede none">Loading the latest snapshot…</p>
        ) : (
          <p className="lede none">
            No counts available. The agent publishes this table with every snapshot; if it is empty
            the snapshot has not been read yet, and inventing the rows would defeat the page.
          </p>
        )}
        </div>

        <h2 id="treasury">Treasury</h2>
        <p className="rule">{SWEEP_RULE_TEXT}</p>

        <p className="lede">
          {BRAND.treasuryAddress ? (
            <>
              Treasury wallet:{' '}
              <a
                href={explorerAddressUrl(BRAND.treasuryAddress)}
                rel="noreferrer noopener"
                target="_blank"
              >
                {BRAND.treasuryAddress}
              </a>
              . Every buy below is a transaction on that wallet.
            </>
          ) : (
            <>
              The treasury wallet address is published here and pinned on {BRAND.handle} before the
              first sweep. Nothing has been swept yet.
            </>
          )}
        </p>

        {sweeps.length > 0 ? (
          <>
            <table className="sweeps">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Bought</th>
                  <th className="num">USD</th>
                  <th>Transaction</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {sweeps.map((s) => (
                  <tr key={s.id}>
                    <td className="asof">{isoUtc(s.ts)}</td>
                    <td className="label">{s.symbol ?? DASH}</td>
                    <td className={s.usd === null ? 'num none' : 'num'}>
                      {s.usd === null ? DASH : usd(s.usd)}
                    </td>
                    <td className="src">
                      {s.txHash ? (
                        <a href={explorerTxUrl(s.txHash)} rel="noreferrer noopener" target="_blank">
                          {shortHash(s.txHash)}
                        </a>
                      ) : (
                        DASH
                      )}
                    </td>
                    <td className="src">{s.note ?? DASH}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="label">Total recorded</td>
                  <td />
                  <td className="num">{usd(swept)}</td>
                  <td className="src">
                    {sweeps.length} {sweeps.length === 1 ? 'buy' : 'buys'} in `sweeps`
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
            <p className="lede">
              Each row was decoded from the receipt of the transaction it links to; the USD figure is
              the Chainlink reference price stored nearest that block time. A row flagged{' '}
              <code>latest-ref</code> priced against the newest reference we hold rather than one
              from the block, because no stored reference was within 30 minutes.
            </p>
          </>
        ) : (
          <p className="lede none">
            No sweep recorded yet. The first one lands the Sunday after launch, and this table fills
            itself from the transaction receipts — we publish it whether the week was good or bad.
          </p>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}
